/**
 * 탭별 조회 설정 관리
 * 각 탭에 대해 프로시저명, 파라미터, 필드명 매핑을 정의
 */

import Global from "../../../Services/Common/Global";

// 탭별 필드명 매핑 (공통 필드)
const CommonFieldMapping = {
  fileName: ['fileName', 'FILE_NM', 'FILE_NAME', 'TITLE', 'DOC_NM'],
  date: [
    'date',
    'DATE',
    'REG_DT',
    'CREATE_DATE',
    'createDate',
    'UPDATE_DATE',
    'updateDate',
  ],
  custCd: ['custCd', 'CUST_CD'],
  depCd: ['depCd', 'DEP_CD', 'DEP_CODE'],
  regUserNm: ['CREATE_ID', 'createId', 'UPDATE_ID', 'updateId', 'REG_USER_NM'],
  // cardTitle: 카드 제목으로 사용할 필드 (선택적, 없으면 fileName 사용)
};

/**
 * 탭별 설정을 가져오는 함수
 * @param {string} tabId - 탭 ID
 * @param {string} menuId - 메뉴 ID
 * @returns {object} 탭 설정 객체
 */
export const GetTabConfig = (tabId, menuId = null) => {
  // 탭별 설정 (모든 설정을 명시적으로 정의)
  const tabSpecificConfigs = {
    /**
     * ============================================================
     * 새로운 탭/메뉴 설정 추가 가이드
     * ============================================================
     *
     * [기본 구조]
     * 탭ID_또는_메뉴ID: {
     *   procedureName: '프로시저명',        // 필수: DB 프로시저 이름
     *   parameterMapping: {                // 필수: 프로시저 파라미터와 searchRef.current 키 매핑
     *     프로시저파라미터(프론트 변수): 'searchRef키(DB 파라미터)',
     *   },
     *   fieldMapping: {                    // 필수: DB 필드명과 화면 표시 필드명 매핑
     *     ...CommonFieldMapping,           // 공통 필드 매핑 상속
     *     cardTitle: [...],                 // 선택: MST 리스트 카드 제목으로 사용할 필드 배열
     *     // 추가 필드 매핑 (선택적)
     *   },
     *   // 선택적 필드들
     *   caseType: 'other',                 // 선택: 'other'일 경우 특별한 로직 사용 (기본값: 'file')
     *   screentType: 'screen',             // 선택: 'screen' 한 장만, 'data' 여러 줄 (기본값: 'data')
     *   printItem: [...],                  // 선택: 카드마다 표시할 인쇄물 버튼 목록
     *   showPrintButton: true,             // 선택: 상단 인쇄 버튼 표시 여부
     *   hidePrintItems: true,              // 선택: 카드 내 인쇄물 버튼 숨김 여부 (MST 리스트에서만)
     *   topPrintButtons: [...],            // 선택: 상단에 표시할 여러 인쇄 버튼 목록 (showPrintButton 대체)
     *   hasDetail: true,                   // 선택: 리스트 클릭 시 상세 화면 표시 여부
     *   detailIdField: 'seq',              // 선택: 상세 조회에 사용할 ID 필드명 (hasDetail이 true일 때 필수)
     *   detailCardTitle: [...],            // 선택: DTL 리스트(상세 화면)에서 사용할 카드 제목 필드 배열
     *   showFilterFABInDetail: true,      // 선택: DTL 리스트에서 필터 FAB 표시 여부
     *   detailParameterMapping: {          // 선택: DTL 프로시저 파라미터 매핑 (hasDetail이 true일 때 사용)
     *     _PARTNER_CD: '_PARTNER_CD',      // detailIdField에서 자동 설정됨
     *     _START_YEAR: '_startYear',       // DTL 조회 시 사용할 추가 파라미터
     *     _END_YEAR: '_endYear',
     *   },
     * }
     *
     * [예시 1: 기본 설정 (탭이 있는 경우)]
     * POL_BASIC_01: {
     *   procedureName: 'SP_POL_BASIC_01_R1',
     *   parameterMapping: {
     *     _CO_CD: '_coCd',                 // 프로시저의 _CO_CD 파라미터 = searchRef.current._coCd
     *     _START_DATE: '_startDate',       // 프로시저의 _START_DATE 파라미터 = searchRef.current._startDate
     *     _END_DATE: '_endDate',
     *     _SEARCH_TEXT: '_searchText',
     *   },
     *   fieldMapping: {
     *     ...CommonFieldMapping,           // fileName, date, regUserNm 등 공통 필드 자동 포함
     *   },
     * },
     *
     * [예시 2: 탭이 없는 경우 (메뉴 ID로 사용)]
     * NoticeBoard: {                       // 메뉴 ID를 키로 사용
     *   procedureName: 'SP_NOT_BASIC_02_R1',
     *   parameterMapping: {
     *     _CO_CD: '_CO_CD',                // searchRef.current._CO_CD 값을 프로시저의 _CO_CD로 전달
     *     _SEARCH_TEXT: '_SEARCH_TEXT',
     *   },
     *   fieldMapping: {
     *     ...CommonFieldMapping,
     *   },
     * },
     *
     * [예시 3: 특별한 로직이 필요한 경우 (caseType: 'other')]
     * SAT_BASIC_02: {
     *   procedureName: 'SP_TPA_BASIC_05_R1',
     *   parameterMapping: {
     *     _START_YEAR: '_START_YEAR',
     *     _CUST_CD: '_CUST_CD',
     *   },
     *   fieldMapping: {
     *     ...CommonFieldMapping,
     *   },
     *   caseType: 'other',                 // M_DCM_BASIC_01F.js의 OtherLogic1에서 특별한 로직 처리
     *   // caseType이 'other'인 경우, src/Scripts/DocsManagement/Other/ 폴더에
     *   // {탭ID}F.js 파일을 생성하여 SearchLogic1, PrintLogic1 함수를 구현해야 함
     * },
     *
     * [예시 3-1: Master/Detail 구조 (hasDetail)]
     * SUB_BASIC_01: {
     *   procedureName: 'SP_SUB_BASIC_01_R1',
     *   parameterMapping: {
     *     _CUST_CD: '_CUST_CD',
     *     _SEARCH_TEXT: '_SEARCH_TEXT',
     *   },
     *   fieldMapping: {
     *     ...CommonFieldMapping,
     *     cardTitle: ['CUST_NM'],           // MST 리스트 카드 제목
     *   },
     *   caseType: 'other',
     *   hasDetail: true,                    // 리스트 클릭 시 상세 화면 표시
     *   detailIdField: 'PARTNER_CD',         // 상세 조회에 사용할 ID 필드명
     *   detailCardTitle: ['SBM_YEAR', 'SBM_HALF'], // DTL 리스트 카드 제목
     *   showPrintButton: true,               // 상단 인쇄 버튼 표시
     *   hidePrintItems: true,                // MST 리스트에서 카드 내 인쇄물 버튼 숨김
     *   showFilterFABInDetail: true,       // DTL 리스트에서 필터 FAB 표시
     *   detailParameterMapping: {            // DTL 프로시저 파라미터 매핑
     *     _PARTNER_CD: '_PARTNER_CD',        // detailIdField에서 자동 설정됨
     *     _START_YEAR: '_startYear',         // DTL 조회 시 사용할 파라미터
     *     _END_YEAR: '_endYear',
     *   },
     * },
     *
     * [예시 4: 한 장만 나오는 레포트 (screentType: 'screen')]
     * POL_BASIC_02: {
     *   procedureName: 'SP_POL_BASIC_02_R4',
     *   parameterMapping: {
     *     _CUST_CD: '_custCd',
     *     _YEAR: '_year',
     *     _QUATER: '_quater',
     *   },
     *   fieldMapping: {
     *     ...CommonFieldMapping,
     *   },
     *   screentType: 'screen',             // 첫 번째 데이터만 표시, cardTitle은 파라미터로 자동 생성
     *   // screentType이 'screen'인 경우:
     *   // - DocumentList.js에서 첫 번째 항목만 표시
     *   // - cardTitle은 _YEAR | _QUATER | 사업장명 | 탭이름 형식으로 자동 생성
     * },
     *
     * [예시 5: 여러 인쇄물 버튼이 있는 경우 (printItem)]
     * BUD_BASIC_02: {
     *   procedureName: 'SP_BUD_BASIC_R2',
     *   parameterMapping: {
     *     _CUST_CD: '_CUST_CD',
     *     _YEAR: '_BUD_YEAR',
     *   },
     *   fieldMapping: {
     *     ...CommonFieldMapping,
     *   },
     *   printItem: [
     *     {
     *       label: '예산편성(세부)',        // 버튼 텍스트
     *       color: '#42A5F5',              // 아이콘 색상 (선택적)
     *       printType: '1',                 // 인쇄 타입 구분자 (M_DCM_BASIC_01F.js에서 사용)
     *     },
     *     {
     *       label: '예산안',
     *       printType: '2',
     *     },
     *   ],
     *   caseType: 'other',
     * },
     *
     * [예시 5-1: 상단 여러 인쇄 버튼 (topPrintButtons)]
     * RIS_BASIC_02: {
     *   procedureName: 'SP_RIS_BASIC_01_R1',
     *   parameterMapping: {
     *     _CUST_CD: '_CUST_CD',
     *     _START_DATE: '_START_DATE',
     *     _END_DATE: '_END_DATE',
     *     _SEARCH_TEXT: '_SEARCH_TEXT',
     *   },
     *   fieldMapping: {
     *     ...CommonFieldMapping,
     *     cardTitle: ['RS_NM'],
     *   },
     *   printItem: [
     *     {
     *       label: '근로자 참여 체크리스트',  // 카드 내 인쇄물 버튼
     *       printType: '1',
     *     },
     *   ],
     *   showPrintButton: true,               // 상단 인쇄 버튼 표시
     *   topPrintButtons: [                   // 상단에 여러 인쇄 버튼 표시
     *     {
     *       label: '위험성평가 결과서',
     *       printType: '2',
     *     },
     *     {
     *       label: '위험성평가 실시규정',
     *       printType: '3',
     *     },
     *     {
     *       label: '근로자참여 서명지',
     *       printType: '4',
     *     },
     *   ],
     *   caseType: 'other',
     * },
     *
     * [예시 6: 카드 제목을 특정 필드로 지정 (cardTitle)]
     * HAM_BASIC_02: {
     *   procedureName: 'SP_HAM_BASIC_03_R1',
     *   parameterMapping: {
     *     _CUST_CD: '_CUST_CD',
     *   },
     *   fieldMapping: {
     *     ...CommonFieldMapping,
     *     cardTitle: ['PRODUCT_NAME', 'WORKER_NAME', 'PROCESS_TYPE'],
     *     // cardTitle: 여러 필드를 |로 구분하여 표시 (MST 리스트에서 사용)
     *     // 예: "제품명 | 작업자명 | 공정유형"
     *     // 값이 없는 필드는 자동으로 제외됨
     *   },
     *   caseType: 'other',
     * },
     *
     * [예시 6-1: MST와 DTL에서 다른 카드 제목 사용]
     * SAP_BASIC_02: {
     *   procedureName: 'SP_SAP_BASIC_02_MST_R1',
     *   parameterMapping: {
     *     _CUST_CD: '_custCd',
     *     _SEARCH_TEXT: '_searchText',
     *   },
     *   fieldMapping: {
     *     ...CommonFieldMapping,
     *     cardTitle: ['manageName', 'managePosition', 'manageDepartment'], // MST 리스트 카드 제목
     *   },
     *   caseType: 'other',
     *   hasDetail: true,
     *   detailIdField: 'seq',
     *   detailCardTitle: ['name', 'evaStartDate', 'evaEndDate'], // DTL 리스트 카드 제목
     * },
     *
     * [주요 파라미터 매핑 규칙]
     * - _YEAR: 년도 선택 (FilterBottomSheet.js에서 년도 필터 표시)
     * - _QUATER: 분기 선택 (_YEAR와 함께 있으면 년도/분기 필터 표시)
     * - _MONTH: 월 선택 (_YEAR와 함께 있으면 년도/월 필터 표시)
     * - _YEAR_MONTH: 조회연월 (YYYYMM 형식, 년도/월 필터 표시)
     * - _CUST_CD: 사업장 코드 (사업장 필터 표시)
     * - _DEP_CD: 부서 코드 (부서 필터 표시)
     * - _START_DATE, _END_DATE: 날짜 범위 (날짜 필터 표시)
     * - _SEARCH_TEXT: 검색어 (검색 필터 표시)
     *
     * [주의사항]
     * 1. caseType이 'other'인 경우, 반드시 Other 폴더에 {탭ID}F.js 파일을 생성해야 함
     * 2. screentType이 'screen'인 경우, cardTitle은 파라미터로 자동 생성되므로 fieldMapping에 지정 불필요
     * 3. printItem의 printType은 숫자 문자열('1', '2', ...)로 지정
     * 4. parameterMapping의 키는 프로시저 파라미터명과 정확히 일치해야 함
     * 5. fieldMapping의 값은 DB에서 반환되는 실제 필드명과 일치해야 함
     * 6. hasDetail이 true인 경우, detailIdField는 필수이며 상세 조회에 사용됨
     * 7. detailParameterMapping의 _PARTNER_CD(또는 detailIdField 값)는 자동으로 설정되므로 명시할 필요 없음
     * 8. topPrintButtons가 있으면 showPrintButton은 무시되고 topPrintButtons만 표시됨
     * 9. hidePrintItems는 MST 리스트에서만 적용되며, DTL 리스트에서는 항상 표시됨
     * 10. showFilterFABInDetail이 true인 경우, DTL 리스트에서 필터 FAB가 표시되고 detailParameterMapping 사용
     *
     * [작업 순서]
     * 1. 프로시저명 확인 및 파라미터 목록 파악
     * 2. searchRef.current에 저장되는 키 이름 확인
     * 3. DB 반환 필드명 확인
     * 4. 위 예시를 참고하여 설정 추가
     * 5. caseType이 'other'인 경우, Other 폴더에 로직 파일 생성
     *    - SearchLogic1: 조회 로직 (필요시)
     *    - PrintLogic1, PrintLogic2, ...: 인쇄 로직 (printItem, topPrintButtons에 따라)
     *    - SearchLogic3: 상세 조회 로직 (hasDetail이 true인 경우)
     *    - PrintLogic2: 상세 인쇄 로직 (hasDetail이 true이고 DTL 인쇄가 필요한 경우)
     * 6. hasDetail이 true인 경우, detailIdField와 detailCardTitle 설정
     * 7. DTL에서 필터가 필요한 경우, showFilterFABInDetail과 detailParameterMapping 설정
     * 8. 테스트 및 검증
     *
     * ============================================================
     */

    //#region 안전보건방침 및 목표
    // 안전보건방침 및 목표 - 안전보건경영방침 (탭 ID: POL_BASIC_01)
    POL_BASIC_01: {
      apiUrl: Global.urls.DocsManagement.getPolBasicR1,
      parameterMapping: {
        coCd: 'CoCd',
        startDate: 'StartDate',
        endDate: 'EndDate',
        searchText: 'SearchText',
      },
      fieldMapping: {
        ...CommonFieldMapping,
      },
    },
    // 안전보건방침 및 목표 - 안전보건목표 및 추진계획 (탭 ID: POL_BASIC_02)
    POL_BASIC_02: {
      apiUrl: Global.urls.DocsManagement.getPolBasic02R1,
      parameterMapping: {
        custCd: 'CustCd',
        year: 'Year',
        quarter: 'Quater',
      },
      fieldMapping: {
        ...CommonFieldMapping,
      },
      caseType: 'other', // other: 해당 페이지만의 특별한 로직 사용
      screentType: 'screen', // screen 한장만 나오는 레포트, data(기본값) 한줄마다 나오는 레포트
    },
    //#endregion
    //#region 예산 편성 및 집행
    // 예산 편성 및 집행 - 안전보건관리결산(집행내역)
    BUD_BASIC_01: {
      apiUrl: Global.urls.DocsManagement.getBudBasicR2,
      parameterMapping: {
        custCd: 'CustCd',
        year: 'BudYear',
        budType: 'BudType',
        userId: 'UserId',
      },
      fieldMapping: {
        ...CommonFieldMapping,
      },
      caseType: 'other', // other: 해당 페이지만의 특별한 로직 사용
      screentType: 'screen',
    },
    // 예산 편성 및 집행 - 안전보건관리 예산편성(세부)
    BUD_BASIC_02: {
      apiUrl: Global.urls.DocsManagement.getBudBasicR2,
      parameterMapping: {
        custCd: 'CustCd',
        year: 'BudYear',
        budType: 'BudType',
        userId: 'UserId',
      },
      fieldMapping: {
        ...CommonFieldMapping,
      },
      // printItem: 카드마다 표시할 인쇄물 버튼 목록
      printItem: [
        {
          label: '예산편성(세부)', // 버튼 텍스트
          color: '#42A5F5', // 아이콘 색상 (선택적, 기본값: '#42A5F5')
          printType: '1', // 인쇄 타입 구분자
        },
        {
          label: '예산안', // 버튼 텍스트
          printType: '2', // 인쇄 타입 구분자
        },
      ],
      caseType: 'other', // other: 해당 페이지만의 특별한 로직 사용
      screentType: 'screen',
    },
    // 예산 편성 및 집행 - 자사양식
    BUD_BASIC_03: {
      apiUrl: Global.urls.DocsManagement.getBudFileR1,
      parameterMapping: {
        custCd: 'custCd',
      },
      fieldMapping: {
        ...CommonFieldMapping,
      },
    },
    //#endregion
    //#region 안전보건인력
    // 안전보건인력 - 전담조직 (탭 ID: SAP_BASIC_01)
    SAP_BASIC_01: {
      apiUrl: Global.urls.DocsManagement.getSapBasic01R1,
      parameterMapping: {
        custCd: 'CustCd',
      },
      fieldMapping: {
        ...CommonFieldMapping,
      },
      caseType: 'other', // other: 해당 페이지만의 특별한 로직 사용
      screentType: 'screen', // screen 한장만 나오는 레포트, data(기본값) 한줄마다 나오는 레포트
    },
    // 안전보건인력 - 안전보건인력 (탭 ID: SAP_BASIC_02)
    SAP_BASIC_02: {
      apiUrl: Global.urls.DocsManagement.getSapBasic02R1,
      parameterMapping: {
        custCd: 'CustCd',
        searchText: 'SearchText',
      },
      fieldMapping: {
        ...CommonFieldMapping,
        cardTitle: ['managerNm', 'position', 'depNm'], // MST 리스트에서 사용
      },
      caseType: 'other', // other: 해당 페이지만의 특별한 로직 사용
      hasDetail: true, // 리스트 클릭 시 상세 화면 표시
      detailIdField: 'managerId', // 상세 조회에 사용할 ID 필드명 (other 로직에서 idValue 추출에 사용)
      detailCardTitle: ['managerNm', 'evaStartDate', 'evaEndDate'], // DTL 리스트(상세 화면)에서 사용
    },
    //#endregion
    //#region 도급사업관리
    // 도급사업관리 - 수급업체
    SUB_BASIC_01: {
      apiUrl:
        Global.urls.DocsManagement.getSubBasic01R1,
      parameterMapping: {
        custCd: 'CustCd',
        searchText: 'SearchText',
      },
      fieldMapping: {
        ...CommonFieldMapping,
        cardTitle: ['custNm'],
      },
      caseType: 'other', // other: 해당 페이지만의 특별한 로직 사용
      screentType: 'screen',
    },
    // 도급사업관리 - 평가 결과
    SUB_BASIC_02: {
      apiUrl:
        Global.urls.DocsManagement.getSubBasicMst02R1,
      parameterMapping: {
        custCd: 'CustCd',
        startYear: 'StartYear',
        endYear: 'EndYear',
        searchText: 'SearchText',
      },
      fieldMapping: {
        ...CommonFieldMapping,
        cardTitle: ['custNm', 'sbmYear', 'sbmHalf'],
      },
      caseType: 'other', // other: 해당 페이지만의 특별한 로직 사용
    },
    // 도급사업관리 - 도급사업 안전보건 협의체 회의록
    SUB_BASIC_03: {
      apiUrl:
        Global.urls.DocsManagement.getSubBasicMst03R1,
      parameterMapping: {
        year: 'ProceedingsYear',
        custCd: 'CustCd',
        startDate: 'StartDate',
        endDate: 'EndDate',
        searchText: 'SearchText',
      },
      fieldMapping: {
        ...CommonFieldMapping,
        cardTitle: ['proceedingsTitle'],
      },
      caseType: 'other', // other: 해당 페이지만의 특별한 로직 사용
    },
    //#endregion
    //#region 위험성평가
    // 위험성평가 - 위험성평가표 (탭 ID: RIS_BASIC_01)
    RIS_BASIC_01: {
      apiUrl:
        Global.urls.DocsManagement.getRisBasic01R1,
      parameterMapping: {
        custCd: 'CustCd',
        startDate: 'StartDate',
        endDate: 'EndDate',
        searchText: 'SearchText',
      },
      fieldMapping: {
        ...CommonFieldMapping,
        cardTitle: ['rsNm'],
      },
      printItem: [
        {
          label: '위험정보 조사표',
          printType: '1',
        },
      ],
      caseType: 'other', // other: 해당 페이지만의 특별한 로직 사용
      showPrintButton: true,
    },
    // 위험성평가 - 위험성평가 결과서 (탭 ID: RIS_BASIC_02)
    RIS_BASIC_02: {
      apiUrl:
        Global.urls.DocsManagement.getRisBasic01R1,
      parameterMapping: {
        custCd: 'CustCd',
        startDate: 'StartDate',
        endDate: 'EndDate',
        searchText: 'SearchText',
      },
      fieldMapping: {
        ...CommonFieldMapping,
        cardTitle: ['rsNm'],
      },
      printItem: [
        {
          label: '근로자 참여 체크리스트',
          printType: '1',
        },
        {
          label: '위험성평가 결과서',
          printType: '2',
        },
      ],
      caseType: 'other', // other: 해당 페이지만의 특별한 로직 사용
      showPrintButton: true,
      topPrintButtons: [
        {
          label: '위험성평가 실시규정',
          printType: '3',
        },
        {
          label: '근로자참여 서명지',
          printType: '4',
        },
      ],
    },
    //#endregion
    //#region 안전작업 관리
    // 안전작업 관리 - 작업계획서 (탭 ID: SAF_BASIC_01)
    SAF_BASIC_01: {
      procedureName: 'SP_SAF_BASIC_01_R1',
      parameterMapping: {
        _CUST_CD: '_custCd',
        _START_DATE: '_startDate',
        _END_DATE: '_endDate',
        _DEP_CD: '_depCd',
        _SEARCH_TEXT: '_searchText',
      },
      fieldMapping: {
        ...CommonFieldMapping,
      },
    },
    // 안전작업 관리 - 안전작업 허가서
    SAF_BASIC_02: {
      procedureName: 'SP_PERMIT_W_R1',
      parameterMapping: {
        _CUST_CD: '_CUST_CD',
        _DEP_CD: '_DEP_CD',
        _START_DATE: '_WORK_START_DATE',
        _END_DATE: '_WORK_END_DATE',
        _GUBUN: '_GUBUN',
        _SEARCH_TEXT: '_SEARCH_TEXT',
      },
      fieldMapping: {
        ...CommonFieldMapping,
        cardTitle: ['USER_NM', 'CREATE_DATE', 'CONSTRUCTION_NM'],
      },
      caseType: 'other', // other: 해당 페이지만의 특별한 로직 사용
    },
    // 안전작업 관리(작업종류별) - 작업계획서 (탭 ID: SAM_BASIC_01)
    SAM_BASIC_01: {
      apiUrl:
        Global.urls.DocsManagement.getSamBasic01R1,
      parameterMapping: {
        custCd: 'CustCd',
        startDate: 'StartDate',
        endDate: 'EndDate',
        depCd: 'DepCd',
        searchText: 'SearchText',
      },
      fieldMapping: {
        ...CommonFieldMapping,
      },
    },
    // 안전작업 관리(작업종류별) - 안전작업 허가서
    SAM_BASIC_02: {
      apiUrl:
        Global.urls.DocsManagement.getSamBasic02R1,
      parameterMapping: {
        custCd: 'CustCd',
        depCd: 'DepCd',
        startDate: 'WorkStartDate',
        endDate: 'WorkEndDate',
        gubun: 'Gubun',
        searchText: 'SearchText',
      },
      fieldMapping: {
        ...CommonFieldMapping,
        cardTitle: ['userName', 'createDate', 'constructionNm'],
      },
      caseType: 'other', // other: 해당 페이지만의 특별한 로직 사용
    },
    //#endregion
    //#region 보호구 관리
    // 보호구 관리 - 보호구 관리대장
    PPE_BASIC_02: {
      apiUrl:
        Global.urls.DocsManagement.getPpeBasic02R1,
      parameterMapping: {
        yearMonth: 'YearMonth',
        custCd: 'CustCd',
      },
      fieldMapping: {
        ...CommonFieldMapping,
      },
      caseType: 'other', // other: 해당 페이지만의 특별한 로직 사용
      screentType: 'screen',
    },
    // 보호구 관리 - 보호구 지급대장
    PPE_BASIC_03: {
      apiUrl:
        Global.urls.DocsManagement.getPpeBasic03R1,
      parameterMapping: {
        year: 'Year',
        custCd: 'CustCd',
      },
      fieldMapping: {
        ...CommonFieldMapping,
      },
      caseType: 'other', // other: 해당 페이지만의 특별한 로직 사용
      screentType: 'screen',
    },
    //#endregion
    //#region 유해·위험 기계·기구
    // 유해·위험 기계·기구 - 안전검사
    HAZ_BASIC_01: {
      apiUrl:
        Global.urls.DocsManagement.getHazBasic01R2,
      parameterMapping: {
        custCd: 'CustCd',
      },
      fieldMapping: {
        ...CommonFieldMapping,
      },
      caseType: 'other', // other: 해당 페이지만의 특별한 로직 사용
      screentType: 'screen',
    },
    // 유해·위험 기계·기구 - 계측기
    HAZ_BASIC_03: {
      apiUrl:
        Global.urls.DocsManagement.getHazBasic03R1,
      parameterMapping: {
        year: 'Year',
        custCd: 'CustCd',
      },
      fieldMapping: {
        ...CommonFieldMapping,
      },
      caseType: 'other', // other: 해당 페이지만의 특별한 로직 사용
      screentType: 'screen',
    },
    //#endregion
    //#region TBM
    // TBM - 교육현황
    TRA_BASIC_01: {
      procedureName: 'SP_TRA_BASIC_01_R1',
      parameterMapping: {
        _YEAR: '_YEAR',
        _MONTH: '_MONTH',
        _CUST_CD: '_CUST_CD',
        _SEARCH_TEXT: '_SEARCH_TEXT',
      },
      fieldMapping: {
        ...CommonFieldMapping,
      },
      caseType: 'other', // other: 해당 페이지만의 특별한 로직 사용
      screentType: 'screen',
    },
    // TBM(CUSTOM) - 교육현황
    TRA_CUSTOM_01: {
      apiUrl:
        Global.urls.DocsManagement.getTraCustom01R1,
      parameterMapping: {
        year: 'Year',
        month: 'Month',
        custCd: 'CustCd',
      },
      fieldMapping: {
        ...CommonFieldMapping,
        cardTitle: ['name', 'tbmAddDate', 'achievementRate'],
      },
      caseType: 'other', // other: 해당 페이지만의 특별한 로직 사용
    },
    // TBM(CUSTOM) - TBM
    TRA_CUSTOM_02: {
      apiUrl:
        Global.urls.DocsManagement.getTraCustom05R1,
      parameterMapping: {
        year: 'Year',
        month: 'Month',
        custCd: 'CustCd',
        searchText: 'SearchText',
      },
      fieldMapping: {
        ...CommonFieldMapping,
        cardTitle: ['tbmTitle', 'tbmDate', 'tbmAddress'],
      },
      caseType: 'other', // other: 해당 페이지만의 특별한 로직 사용
    },
    //#endregion
    //#region 안전보건교육
    // 안전보건교육 - 연간교육 계획표
    SAT_BASIC_01: {
      apiUrl:
        Global.urls.DocsManagement.getSatBasic03R1,
      parameterMapping: {
        custCd: 'CustCd',
        year: 'SafetyDate',
        userId: 'UserId',
        searchText: 'SearchText',
      },
      fieldMapping: {
        ...CommonFieldMapping,
      },
      caseType: 'other', // other: 해당 페이지만의 특별한 로직 사용
      screentType: 'screen',
    },
    // 안전보건교육 - 교육일지
    SAT_BASIC_02: {
      apiUrl:
        Global.urls.DocsManagement.getTraCustom02R1,
      parameterMapping: {
        custCd: 'CustCd',
        startDate: 'StartDt',
        endDate: 'EndDt',
        searchText: 'SearchText',
      },
      fieldMapping: {
        ...CommonFieldMapping,
        cardTitle: ['eduTypeNm'],
      },
      caseType: 'other', // other: 해당 페이지만의 특별한 로직 사용
    },
    // 안전보건교육 - 교육이수현황
    SAT_BASIC_03: {
      apiUrl:
        Global.urls.DocsManagement.getSatBasic05R1,
      parameterMapping: {
        custCd: 'CustCd',
        year: 'StartYear',
        searchText: 'SearchText',
      },
      fieldMapping: {
        ...CommonFieldMapping,
      },
      caseType: 'other', // other: 해당 페이지만의 특별한 로직 사용
      screentType: 'screen',
    },
    // 안전보건교육 - 교육관리(교육별)
    SAT_BASIC_04: {
      apiUrl:
        Global.urls.DocsManagement.getSatBasic05R1,
      parameterMapping: {
        custCd: 'CustCd',
        year: 'StartYear',
        searchText: 'SearchText',
      },
      fieldMapping: {
        ...CommonFieldMapping,
      },
      caseType: 'other', // other: 해당 페이지만의 특별한 로직 사용
      screentType: 'screen',
    },
    // 안전보건교육 - 교육관리(대상자별)
    SAT_BASIC_05: {
      apiUrl:
        Global.urls.DocsManagement.getSatBasic07R1,
      parameterMapping: {
        custCd: 'CustCd',
        year: 'StartYear',
      },
      fieldMapping: {
        ...CommonFieldMapping,
      },
      caseType: 'other', // other: 해당 페이지만의 특별한 로직 사용
      screentType: 'screen',
    },
    //#endregion
    //#region 유해·위험물질
    // 유해·위험물질 - 물질안전보건자료(MSDS) (탭 ID: HAM_BASIC_01)
    HAM_BASIC_01: {
      apiUrl:
        Global.urls.DocsManagement.getHamBasic01R1,
      parameterMapping: {
        custCd: 'CustCd',
        startDate: 'StartDate',
        endDate: 'EndDate',
        searchText: 'SearchText',
      },
      fieldMapping: {
        ...CommonFieldMapping,
      },
    },
    // 유해·위험물질 - MSDS 관리대장 (탭 ID: HAM_BASIC_02)
    HAM_BASIC_02: {
      apiUrl:
        Global.urls.DocsManagement.getHamBasic02R1,
      parameterMapping: {
        custCd: 'CustCd',
      },
      fieldMapping: {
        ...CommonFieldMapping,
      },
      caseType: 'other', // other: 해당 페이지만의 특별한 로직 사용
      screentType: 'screen',
    },
    //#endregion
    //#region 일반/특수 건강진단
    // 일반/특수 건강진단 - 일반건강진단
    HEA_BASIC_01: {
      apiUrl:
        Global.urls.DocsManagement.getHeaBasic01R1,
      parameterMapping: {
        custCd: 'CustCd',
        searchText: 'SearchText',
      },
      fieldMapping: {
        ...CommonFieldMapping,
      },
      printItem: [
        {
          label: '일반건강진단',
          printType: '1',
        },
        {
          label: '건강진단 실시 지침서',
          printType: '2',
        },
      ],
      caseType: 'other', // other: 해당 페이지만의 특별한 로직 사용
      screentType: 'screen',
    },
    // 일반/특수 건강진단 - 특수건강진단
    HEA_BASIC_02: {
      apiUrl:
        Global.urls.DocsManagement.getHeaBasic02R1,
      parameterMapping: {
        custCd: 'CustCd',
        searchText: 'SearchText',
      },
      fieldMapping: {
        ...CommonFieldMapping,
      },
      printItem: [
        {
          label: '특수건강진단',
          printType: '1',
        },
        {
          label: '건강진단 실시 지침서',
          printType: '2',
        },
      ],
      caseType: 'other', // other: 해당 페이지만의 특별한 로직 사용
      screentType: 'screen',
    },
    //#endregion
  };

  // 메뉴 ID로도 설정 가능 (탭이 없는 경우)
  const menuSpecificConfigs = {
    // 비상대응매뉴얼 (탭이 없는 메뉴)
    EmergencyManual: {
      apiUrl:
        Global.urls.DocsManagement.getEmrBasicR1,
      parameterMapping: {
        custCd: 'CustCd',
        startDate: 'StartDate',
        endDate: 'EndDate',
        searchText: 'SearchText',
      },
      fieldMapping: {
        ...CommonFieldMapping,
      },
    },
    // 법규준수평가
    LegalCompliance: {
      apiUrl:
        Global.urls.DocsManagement.getLegBasicR1,
      parameterMapping: {
        year: 'LwYear',
        custCd: 'CustCd',
        searchText: 'SearchText',
      },
      fieldMapping: {
        ...CommonFieldMapping,
        cardTitle: ['lwBranch', 'lwEvaStartDate', 'lwEvaEndDate'],
      },
      caseType: 'other', // other: 해당 페이지만의 특별한 로직 사용
    },
    // 중대재해처벌법 이행관리
    SeriousAccidentAct: {
      apiUrl:
        Global.urls.DocsManagement.getSerBasicR1,
      parameterMapping: {
        custCd: 'CustCd',
        searchText: 'SearchText',
      },
      fieldMapping: {
        ...CommonFieldMapping,
        cardTitle: ['evaDate', 'evaName', 'evaPoint'],
      },
      caseType: 'other', // other: 해당 페이지만의 특별한 로직 사용
    },
    // 산업안전보건위원회
    SafetyCommittee: {
      apiUrl:
        Global.urls.DocsManagement.getSacBasicR1,
      parameterMapping: {
        custCd: 'CustCd',
        year: 'ProceedingsYear',
        quarter: 'QuarterlyOrder',
      },
      fieldMapping: {
        ...CommonFieldMapping,
        cardTitle: ['proceedingsTitle'],
      },
      printItem: [
        {
          label: '회의록',
          printType: '1',
        },
      ],
      caseType: 'other', // other: 해당 페이지만의 특별한 로직 사용
      showPrintButton: true,
    },
    // 작업환경측정 (탭이 없는 메뉴)
    WorkEnvironment: {
      apiUrl:
        Global.urls.DocsManagement.getWorBasicR1,
      parameterMapping: {
        custCd: 'CustCd',
        startDate: 'StartDate',
        endDate: 'EndDate',
        searchText: 'SearchText',
      },
      fieldMapping: {
        ...CommonFieldMapping,
      },
    },
    // 산업재해 보고 및 기록 (탭이 없는 메뉴)
    AccidentReport: {
      apiUrl:
        Global.urls.DocsManagement.getAccBasicR1,
      parameterMapping: {
        custCd: 'CustCd',
        startDate: 'StartDate',
        endDate: 'EndDate',
        searchText: 'SearchText',
      },
      fieldMapping: {
        ...CommonFieldMapping,
      },
    },
  };

  console.log('GetTabConfig - menuSpecificConfigs 정의 완료:', {
    keys: Object.keys(menuSpecificConfigs),
    hasEmergencyManual: 'EmergencyManual' in menuSpecificConfigs,
    EmergencyManual: menuSpecificConfigs['EmergencyManual'],
  });

  // 탭 ID로 먼저 확인
  let specificConfig = tabSpecificConfigs[tabId];

  // 탭 ID로 찾지 못했고 메뉴 ID가 있으면 메뉴 ID로 확인
  if (!specificConfig && menuId) {
    console.log('GetTabConfig - menuSpecificConfigs 확인:', {
      menuId,
      availableKeys: Object.keys(menuSpecificConfigs),
      hasKey: menuId in menuSpecificConfigs,
    });
    specificConfig = menuSpecificConfigs[menuId];
    console.log('GetTabConfig - menuId로 찾기:', {
      menuId,
      found: !!specificConfig,
      procedureName: specificConfig?.procedureName,
    });
  }

  // caseType이 없으면 기본값 'file' 설정
  if (specificConfig && !specificConfig.caseType) {
    specificConfig.caseType = 'file';
  }

  return specificConfig;
};

/**
 * 필드값을 가져오는 헬퍼 함수
 * @param {object} item - 데이터 아이템
 * @param {array} fieldNames - 가능한 필드명 배열
 * @returns {string} 필드값
 */
export const GetFieldValue = (item, fieldNames) => {
  if (!item || !Array.isArray(fieldNames)) return '';

  for (const fieldName of fieldNames) {
    if (item[fieldName] !== undefined && item[fieldName] !== null) {
      return item[fieldName];
    }
  }
  return '';
};
